/**
 * Client-side Setup for Webview
 *
 * Initialize all client-side services and configurations.
 * This file is imported in main.tsx.
 *
 * Note: This is a simplified version without i18n as webview
 * does not require localized routes.
 */
import { setupBrowserApiClients } from '@shared-web/api/client';
import { X_WEB_ENABLE_LOGGING } from '@shared-web/constants/storage';
import { setupTelemetry } from '@shared-web/telemetry/client';

/**
 * Setup all client-side services
 *
 * This function is called once when the application starts in the browser.
 * Add any additional client-side initialization here (e.g., Sentry, Analytics).
 */
export function setupClient(): void {
  // Initialize API clients for browser
  setupBrowserApiClients({
    enableToast: true,
  });

  const appEnv = import.meta.env.VITE_APP_ENV || import.meta.env.MODE;
  const isDevelopment = appEnv === 'development';
  const isDevToolsLoggingEnabled = (() => {
    try {
      return localStorage.getItem(X_WEB_ENABLE_LOGGING) === 'true';
    } catch {
      return false;
    }
  })();
  const shouldEnableTelemetry =
    !isDevelopment || import.meta.env.VITE_OTEL_ENABLE_LOCAL === 'true';

  if (shouldEnableTelemetry) {
    setupTelemetry({
      serviceName: import.meta.env.VITE_OTEL_SERVICE_NAME || 'X-WEB-WEBVIEW',
      serviceVersion: import.meta.env.VITE_APP_VERSION || '0.1.0',
      otlpEndpoint: import.meta.env.VITE_OTEL_EXPORTER_OTLP_TRACES_ENDPOINT,
      logsEndpoint: import.meta.env.VITE_OTEL_EXPORTER_OTLP_LOGS_ENDPOINT,
      metricsEndpoint: import.meta.env.VITE_OTEL_EXPORTER_OTLP_METRICS_ENDPOINT,
      environment: appEnv,
      debug: import.meta.env.VITE_OTEL_DEBUG === 'true',
      enableConsoleLogging: isDevelopment || isDevToolsLoggingEnabled,
    });
  }

  // Log webview environment info for debugging
  console.log('[Webview] Client setup complete', {
    environment: appEnv,
    telemetryEnabled: shouldEnableTelemetry,
  });
}
